<template>
  <div>
    <!--TOOLBAR-->
    <tool-bar title="Edit Service Log"></tool-bar>
    <!--CONTAINER VIEW-->
    <!--STUDENT HEADER-->
    <v-container
      class="page-container"
      :class="{'mobile pa-4': isMobile, 'py-4 px-5': isTablet}">
      <student-card :student="student"></student-card>
      <service-log-form
        :showTooltip="showTooltip"
        :student="student"
        :medicaidArray="medicaidArray"
        :absentTypesArray="absentTypesArray"
        :absentReasonsArray="absentReasonsArray"
        :absentAlternativesArray="absentAlternativesArray"
        :groupTypeArray="groupTypeArray"
        :deliveryModeArray="deliveryModeArray"
        :serviceDeliveredArray="serviceDeliveredArray"
        :supervisorFlagsArray="supervisorFlagsArray"
        :loadData="loadData"
        :index="1"
        :arrayLength="1"
        action="edit"
        v-on:form:submitted="submitted = true"
      ></service-log-form>
    </v-container>
    <bottom-nav></bottom-nav>
    <full-screen-loading-spinner v-if="showLoading"></full-screen-loading-spinner>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import * as _ from 'lodash';
import Swal from 'sweetalert2';
import Toolbar from '@/components/toolbar';
import api from '@/utils/api';
import ServiceLogForm from '@/components/service-log/form/service-log-form';
import StudentCard from '@/components/service-log/form/student-card';
import BottomNav from '@/components/bottom-nav';
import utilityFunctions from '@/utils/functions';
import StudentClass from '@/utils/student-class';
import ServiceLogClass from '@/utils/service-log-class';
import FullScreenLoadingSpinner from '@/components/full-screen-loading-spinner';
import * as Constants from '@/constants';

export default {
  components: {
    ServiceLogForm,
    Toolbar,
    StudentCard,
    BottomNav,
    FullScreenLoadingSpinner
  },
  name: 'SlEdit',
  data() {
    return {
      deliveryModeArray: null,
      serviceDeliveredArray: null,
      groupTypeArray: null,
      loadData: false,
      medicaidArray: null,
      absentTypesArray: null,
      absentReasonsArray: null,
      absentAlternativesArray: null,
      showLoading: false,
      student: null,
      submissionData: null,
      submitted: false,
      supervisorFlagsArray: null
    };
  },
  computed: {
    ...mapGetters({
      showTooltip: 'showTooltip'
    }),
    isMobile() {
      return this.$mq === 'mobile';
    },
    isTablet() {
      return this.$mq === 'tablet';
    }
  },
  methods: {
    hasServiceInformationChanged() {
      return !_.isEqual(Vue.prototype.$savedData.serviceInformation, this.student.form.serviceInformation);
    },
    hasMedicaidInformationChanged() {
      return !_.isEqual(Vue.prototype.$savedData.medicaid, this.student.form.medicaid);
    },
    hasInformationChanged() {
      return this.hasServiceInformationChanged() || this.hasMedicaidInformationChanged();
    },
    handleResetSignature(newVal, oldVal) {
      if (newVal && oldVal) {
        this.student.form.serviceInformation.supervisorUserId = null;
        this.student.form.serviceInformation.supervisorDate = null;
        // Only clear provider checkbox if user editing is not a supervisor
        // OR if they are a supervisor, but log doesn't need supervision
        if (!this.supervisorFlagsArray.canSupervise || (this.supervisorFlagsArray.canSupervise && !this.supervisorFlagsArray.supervisorRequiredFlag)) {
          this.student.form.serviceInformation.licenseAttestUserId = null;
          this.student.form.serviceInformation.licenseAttestDate = null;
        }
      }
    },
    handleResetSignatureNewFields(newValue, oldValue) {
      if ((newValue && oldValue) || (newValue && oldValue === null)) {
        this.student.form.serviceInformation.supervisorUserId = null;
        this.student.form.serviceInformation.supervisorDate = null;
        // Only clear provider checkbox if user editing is not a supervisor
        // OR if they are a supervisor, but log doesn't need supervision
        if (!this.supervisorFlagsArray.canSupervise || (this.supervisorFlagsArray.canSupervise && !this.supervisorFlagsArray.supervisorRequiredFlag)) {
          this.student.form.serviceInformation.licenseAttestUserId = null;
          this.student.form.serviceInformation.licenseAttestName = null;
          this.student.form.serviceInformation.licenseAttestDate = null;
        }
      }
    }
  },
  created() {
    utilityFunctions.clearSubmissionArray();
    const sessionId = this.$route.params.sessionId;
    this.showLoading = true;
    let data;
    Promise.all([
      api.get(Constants.PTSL_GET_PT_SESSION_URL(sessionId)),
      api.get(Constants.GET_MEDICAID_PROCEDURES_LIST_URL()),
      api.get(Constants.ABSENT_TYPES_URL()),
      api.get(Constants.ABSENT_REASONS_URL()),
      api.get(Constants.ABSENT_ALTERNATIVES_URL()),
      api.get(Constants.GROUP_TYPES_URL()),
      api.get(Constants.DELIVERY_MODES_URL()),
      api.get(Constants.SERVICE_DELIVERED_URL()),
      api.get(Constants.GET_SUPERVISOR_FLAGS_URL(sessionId))
    ])
      .then((submission) => {
        /* eslint-disable */
        data = submission[0].data.data;
        // set medicaid procedures array
        this.medicaidArray = submission[1].data.data;
        // set absentTypesArray
        this.absentTypesArray = submission[2].data.data;
        // set absentReasonsArray
        this.absentReasonsArray = submission[3].data.data;
        // set absentAlternativesArray
        this.absentAlternativesArray = submission[4].data.data;
        // set groupTypeArray
        this.groupTypeArray = submission[5].data.data;
        // set deliveryModeArray
        this.deliveryModeArray = submission[6].data.data;
        // set ServiceDeliveredArray
        this.serviceDeliveredArray = submission[7].data.data;
        // set supervisorFlagsArray
        this.supervisorFlagsArray = submission[8].data.data;
        return api.get(Constants.GET_SERVICE_LOCATIONS_URL(data.locationName));
      })
      .then((school) => {
        data.location = school.data.data[0];
        // need to use vue.set to make the information reactive
        Vue.set(this, 'student', new StudentClass(data));
        const info = new ServiceLogClass(data, true, false);
        // save initialized form data to check if data changed before navigating away
        Vue.prototype.$savedData = _.cloneDeep(info);
        // need to use vue.set to make the information reactive
        Vue.set(this.student, 'form', info);
      })
      .finally(() => {
        this.loadData = true;
        this.showLoading = false;
      });
  },
  /** TODO - Refactor watchers for service information object in relation to signature reset. */
  watch: {
    'student.form.serviceInformation.ptSessionBehaviors': {
      handler(newVal, oldVal) {
        this.handleResetSignature(newVal, oldVal);
      }
    },
    'student.form.serviceInformation.ptSessionObjectives': {
      handler(newVal, oldVal) {
        this.handleResetSignature(newVal, oldVal);
      }
    },
    'student.form.serviceInformation.ptSessionProgresses': {
      handler(newVal, oldVal) {
        this.handleResetSignature(newVal, oldVal);
      }
    },
    'student.form.serviceInformation.ptSessionProposedPlan': {
      handler(newVal, oldVal) {
        this.handleResetSignature(newVal, oldVal);
      }
    },
    'student.form.serviceInformation.absentType': {
      handler(newVal, oldVal) {
        this.handleResetSignatureNewFields(newVal, oldVal);
      }
    },
    'student.form.serviceInformation.absentReason': {
      handler(newVal, oldVal) {
        this.handleResetSignatureNewFields(newVal, oldVal);
      }
    },
    'student.form.serviceInformation.absentAlternative': {
      handler(newVal, oldVal) {
        this.handleResetSignatureNewFields(newVal, oldVal);
      }
    },
    'student.form.serviceInformation.noServiceNotifFlag': {
      handler(newVal, oldVal) {
        if ((newVal !== undefined) && (oldVal !== undefined)) {
          this.student.form.serviceInformation.supervisorUserId = null;
          this.student.form.serviceInformation.supervisorDate = null;
          // Only clear provider checkbox if user editing is not a supervisor
          // OR if they are a supervisor, but log doesn't need supervision
          if (!this.supervisorFlagsArray.canSupervise || (this.supervisorFlagsArray.canSupervise && !this.supervisorFlagsArray.supervisorRequiredFlag)) {
            this.student.form.serviceInformation.licenseAttestUserId = null;
            this.student.form.serviceInformation.licenseAttestDate = null;
          }
        }
      }
    },
    'student.form.serviceInformation.absentAlternativeDesc': {
      handler(newVal, oldVal) {
        this.handleResetSignature(newVal, oldVal);
      }
    },
    'student.form.serviceInformation.location': {
        handler(newVal, oldVal) {
          this.handleResetSignature(newVal, oldVal);
        }
      },
    'student.form.serviceInformation.serviceDelivered': {
        handler(newVal, oldVal) {
          this.handleResetSignatureNewFields(newVal, oldVal);
        }
    },
    'student.form.serviceInformation.sessionDate': {
      handler(newVal, oldVal) {
        this.handleResetSignature(newVal, oldVal);
      }
    },
    'student.form.serviceInformation.sessionTime': {
      handler(newVal, oldVal) {
        this.handleResetSignature(newVal, oldVal);
      }
    },
    'student.form.serviceInformation.sessionMinutes': {
      handler(newVal, oldVal) {
        this.handleResetSignature(newVal, oldVal);
      }
    },
    'student.form.serviceInformation.locationOther': {
      handler(newVal, oldVal) {
        this.handleResetSignature(newVal, oldVal);
      }
    },
    'student.form.serviceInformation.groupSize': {
        handler(newVal, oldVal) {
          this.handleResetSignature(newVal, oldVal);
        }
    },
    'student.form.serviceInformation.groupType': {
      handler(newVal, oldVal) {
        this.handleResetSignature(newVal, oldVal);
      }
    },
    'student.form.serviceInformation.deliveryMode': {
      handler(newVal, oldVal) {
        this.handleResetSignature(newVal, oldVal);
      }
    },
    'student.form.serviceInformation.providerLocation': {
      handler(newVal, oldVal) {
        this.handleResetSignature(newVal, oldVal);
      }
    },
    'student.form.serviceInformation.nextSessionFocus': {
        handler(newVal, oldVal) {
          this.handleResetSignature(newVal, oldVal);
        }
    },
    'student.form.serviceInformation.progressAssessment': {
        handler(newVal, oldVal) {
          this.handleResetSignature(newVal, oldVal);
        }
    },
    'student.form.serviceInformation.studentBehavior': {
        handler(newVal, oldVal) {
          this.handleResetSignature(newVal, oldVal);
        }
    },
    'student.form.serviceInformation.nextSessionGoal': {
      handler(newVal, oldVal) {
        this.handleResetSignature(newVal, oldVal);
      }
    },
    'student.form.serviceInformation.notes': {
      handler(newVal, oldVal) {
        this.handleResetSignature(newVal, oldVal);
      }
    },
    'student.form.serviceInformation.sessionNotes': {
      handler(newVal, oldVal) {
        this.handleResetSignature(newVal, oldVal);
      }
    },
    'student.form.medicaid': {
      deep: true,
      handler(newVal, oldVal) {
        this.handleResetSignature(newVal, oldVal);
      }
    },
  },
  beforeRouteLeave(to, from, next) {
    if (!this.submitted) {
      if (this.hasInformationChanged()) {
        Swal.fire({
          text: 'You have unsaved information.  Are you sure you want to leave this page?',
          confirmButtonText: 'OK',
          showCancelButton: true,
          customClass: {
            popup: 'popup-class',
            content: 'content-class',
            confirmButton: 'confirm-button-class'
          }
        }).then((response) => {
          // if ok was clicked
          if (response && response.value) {
            next();
          }
        });
      } else {
        next();
      }
    } else {
      next();
    }
  }
};
</script>

<style lang="scss" scoped></style>



// WEBPACK FOOTER //
// src/views/service-log/edit-service-log.vue